/**
 * Detect the OS silencing our microphone during a call.
 *
 * ── History, so nobody reintroduces the removed parts ───────────────────────
 *
 * Backgrounding the app mid-call makes the OS feed the microphone digital
 * silence: the track stays `live` and `enabled`, the peer connection stays
 * `connected`, RTP keeps flowing — the other side just goes deaf, with no error
 * anywhere. That is an OS restriction; a web app cannot override it.
 *
 * This module used to also carry a "silent keep-alive" audio loop meant to keep
 * the page audible in the background. The WAV it looped had a ZERO-byte data
 * chunk — a 0.000s file with loop=true — so the element's loop cycle spun as
 * fast as the media pipeline allowed and lagged the whole tab the moment a call
 * connected. It did not help on devices that enforce the restriction, and it
 * cost far more than it bought, so it is gone.
 *
 * What remains is the only part unconditionally worth having: the mic track's
 * `mute`/`unmute` events are the single honest signal that capture was cut, so
 * we keep listening and expose it as state (micSilenced) for the UI.
 */
export function attachCallBackgroundGuard(stream, { onMicSilenced } = {}) {
    const track = stream?.getAudioTracks?.()[0];
    if (!track) return () => {};

    const handleMute = () => {
        onMicSilenced?.(true);
    };
    const handleUnmute = () => {
        onMicSilenced?.(false);
    };

    track.addEventListener("mute", handleMute);
    track.addEventListener("unmute", handleUnmute);

    // A track can already be muted by the time we get here.
    if (track.muted) handleMute();

    return () => {
        track.removeEventListener("mute", handleMute);
        track.removeEventListener("unmute", handleUnmute);
    };
}

export default { attachCallBackgroundGuard };
